import { useTranslation } from 'react-i18next'
import { Button, Typogaphy } from 'vnetwork-ui'
import {
    Upload,
    X,
    Download,
    FileSpreadsheet,
    AlertCircle,
    RefreshCw,
} from 'lucide-react'
import * as XLSX from 'xlsx'
import { useState, useMemo, useEffect } from 'react'
import { MailerReceiver, MailerTemplateVariableItem } from '../../types'

interface ColumnMapping {
    [variableName: string]: string
}

interface ReceiverImporterProps {
    receivers: MailerReceiver[]
    onReceiversChange: (receivers: MailerReceiver[], columns: string[]) => void
    disabled?: boolean
    customerVariables?: MailerTemplateVariableItem[]
    hasExistingReceivers?: boolean
}

const ReceiverImporter: React.FC<ReceiverImporterProps> = ({
    receivers,
    onReceiversChange,
    disabled = false,
    customerVariables = [],
    hasExistingReceivers = false,
}) => {
    const { t } = useTranslation()
    const [fileName, setFileName] = useState<string>('')
    const [error, setError] = useState<string>('')
    const [showUploadSection, setShowUploadSection] =
        useState(!hasExistingReceivers)

    useEffect(() => {
        setShowUploadSection(!hasExistingReceivers)
    }, [hasExistingReceivers])

    // Debug
    console.log('ReceiverImporter:', {
        receiversCount: receivers.length,
        hasExistingReceivers,
        showUploadSection,
    })

    const requiredVariables = useMemo(() => {
        const vars: { name: string; label: string }[] = [
            { name: 'email', label: 'Email' },
        ]
        customerVariables.forEach((v) => {
            if (!vars.find((x) => x.name === v.name)) {
                vars.push({ name: v.name, label: v.label })
            }
        })
        return vars
    }, [customerVariables])

    const autoMapColumns = (fileColumns: string[]) => {
        const mapping: ColumnMapping = {}
        const lowerFileColumns = fileColumns.map((c) => c.toLowerCase())

        requiredVariables?.forEach((variable) => {
            const exactIndex = lowerFileColumns?.indexOf(
                variable.name.toLowerCase()
            )
            if (exactIndex !== -1) {
                mapping[variable.name] = fileColumns[exactIndex]
                return
            }

            const labelIndex = lowerFileColumns.indexOf(
                variable.label.toLowerCase()
            )
            if (labelIndex !== -1) {
                mapping[variable.name] = fileColumns[labelIndex]
                return
            }

            const partialIndex = lowerFileColumns.findIndex(
                (c) =>
                    c.includes(variable.name.toLowerCase()) ||
                    variable.name.toLowerCase().includes(c)
            )
            if (partialIndex !== -1) {
                mapping[variable.name] = fileColumns[partialIndex]
            }
        })

        return mapping
    }

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return

        setError('')
        setFileName(file.name)

        const reader = new FileReader()
        reader.onload = (e) => {
            try {
                const data = e.target?.result
                const workbook = XLSX.read(data, { type: 'array' })
                const sheetName = workbook.SheetNames[0]
                const worksheet = workbook.Sheets[sheetName]
                const jsonData = XLSX.utils.sheet_to_json<unknown[]>(
                    worksheet,
                    { header: 1 }
                )

                if (jsonData.length < 2) {
                    setError(t('mailerCampaign:file_empty_or_no_data'))
                    return
                }

                const headers = (jsonData[0] as unknown[])
                    .map((h) => String(h ?? '').trim())
                    .filter(Boolean)

                if (headers.length === 0) {
                    setError(t('mailerCampaign:file_empty_or_no_data'))
                    return
                }

                const parsedData: Record<string, string>[] = []
                for (let i = 1; i < jsonData.length; i++) {
                    const row = jsonData[i] as unknown[]
                    if (!row || row.length === 0) continue

                    const record: Record<string, string> = {}
                    headers.forEach((header, index) => {
                        record[header] = String(row[index] ?? '').trim()
                    })

                    if (Object.values(record).some((v) => v)) {
                        parsedData.push(record)
                    }
                }

                if (parsedData.length === 0) {
                    setError(t('mailerCampaign:no_valid_data_found'))
                    return
                }

                // Auto-map and apply immediately
                const mapping = autoMapColumns(headers)

                if (!mapping.email) {
                    setError(t('mailerCampaign:email_column_required'))
                    return
                }

                const transformedReceivers: MailerReceiver[] = []
                parsedData.forEach((row) => {
                    const receiver: MailerReceiver = { email: '' }

                    Object.entries(mapping).forEach(([varName, fileColumn]) => {
                        if (fileColumn && row[fileColumn]) {
                            receiver[varName] = row[fileColumn]
                        }
                    })

                    headers.forEach((col) => {
                        const lowerCol = col.toLowerCase()
                        if (!receiver[lowerCol] && row[col]) {
                            receiver[lowerCol] = row[col]
                        }
                    })

                    if (receiver.email && receiver.email.includes('@')) {
                        transformedReceivers.push(receiver)
                    }
                })

                if (transformedReceivers.length === 0) {
                    setError(t('mailerCampaign:no_valid_emails_found'))
                    return
                }

                onReceiversChange(
                    transformedReceivers,
                    Object.keys(mapping).filter((k) => mapping[k])
                )
            } catch (err) {
                console.error('File parsing error:', err)
                setError(t('mailerCampaign:file_parsing_error'))
            }
        }

        reader.readAsArrayBuffer(file)
        event.target.value = ''
    }

    const handleClear = () => {
        setFileName('')
        setError('')
        onReceiversChange([], [])
    }

    const handleNewUpload = () => {
        setShowUploadSection(true)
        handleClear()
    }

    const downloadTemplate = () => {
        const columns = ['email']
        const exampleValues: Record<string, string> = {
            email: 'example@company.com',
        }
        const exampleValues2: Record<string, string> = {
            email: 'jane@example.com',
        }

        customerVariables.forEach((v) => {
            if (!columns.includes(v.name)) {
                columns.push(v.name)
                exampleValues[v.name] = v.label
                    ? `[${v.label} 1]`
                    : `[${v.name} 1]`
                exampleValues2[v.name] = v.label
                    ? `[${v.label} 2]`
                    : `[${v.name} 2]`
            }
        })

        if (columns.length === 1) {
            columns.push('name', 'company')
            exampleValues.name = 'John Doe'
            exampleValues.company = 'ACME Corp'
            exampleValues2.name = 'Jane Smith'
            exampleValues2.company = 'Tech Inc'
        }

        const templateData = [
            columns,
            columns.map((col) => exampleValues[col] || ''),
            columns.map((col) => exampleValues2[col] || ''),
        ]

        const ws = XLSX.utils.aoa_to_sheet(templateData)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Receivers')
        XLSX.writeFile(wb, 'receiver_template.xlsx')
    }

    // Show upload section if: no existing receivers OR user clicked "new upload"
    const shouldShowUploadInput =
        !hasExistingReceivers || receivers.length === 0 || showUploadSection

    return (
        <div className="space-y-2">
            {/* Upload Controls */}
            <div className="flex items-center gap-3 flex-wrap">
                {shouldShowUploadInput ? (
                    <>
                        <div
                            className={`relative flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-md transition-colors ${disabled ? 'opacity-50' : 'hover:border-blue-500 hover:bg-blue-50'}`}
                        >
                            <label
                                className={`flex items-center gap-2 cursor-pointer ${disabled ? 'cursor-not-allowed' : ''}`}
                            >
                                <Upload size={16} className="text-gray-500" />
                                <span className="text-sm text-gray-600 max-w-[200px] truncate">
                                    {fileName ||
                                        t(
                                            'mailerCampaign:upload_receiver_file'
                                        )}
                                </span>
                                <input
                                    type="file"
                                    accept=".xlsx,.xls,.csv"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    disabled={disabled}
                                />
                            </label>
                            {fileName && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        handleClear()
                                    }}
                                    className="p-0.5 rounded-full hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
                                    disabled={disabled}
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        <Button
                            variant="text"
                            size="small"
                            onClick={downloadTemplate}
                            className="!text-blue-600"
                        >
                            <Download size={16} className="mr-1" />
                            {t('mailerCampaign:download_template')}
                        </Button>
                    </>
                ) : (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleNewUpload}
                        className="!text-blue-600"
                    >
                        <RefreshCw size={16} className="mr-1" />
                        {t('mailerCampaign:new_upload')}
                    </Button>
                )}
            </div>

            {/* Warning for existing receivers - show when user is about to upload new file */}
            {hasExistingReceivers &&
                receivers.length > 0 &&
                showUploadSection && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm flex items-center gap-2">
                        <AlertCircle size={16} className="flex-shrink-0" />
                        <span>
                            {t(
                                'mailerCampaign:upload_will_replace_existing_receivers'
                            )}
                        </span>
                    </div>
                )}

            {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                    <AlertCircle size={16} />
                    {error}
                </div>
            )}

            {/* Preview Table */}
            {receivers.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b flex items-center gap-2">
                        <FileSpreadsheet size={18} className="text-green-600" />
                        <Typogaphy className="text-sm font-medium">
                            {t('mailerCampaign:imported_receivers')}:{' '}
                            {receivers.length}
                        </Typogaphy>
                    </div>
                    <div className="max-h-[250px] overflow-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                                        #
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                                        Email
                                    </th>
                                    {customerVariables
                                        .filter((v) => v.name !== 'email')
                                        .map((v) => (
                                            <th
                                                key={v.name}
                                                className="px-3 py-2 text-left text-xs font-semibold text-gray-600"
                                            >
                                                {v.label}
                                            </th>
                                        ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {receivers
                                    .slice(0, 50)
                                    .map((receiver, index) => (
                                        <tr
                                            key={index}
                                            className={
                                                index % 2 === 0
                                                    ? 'bg-white'
                                                    : 'bg-gray-50'
                                            }
                                        >
                                            <td className="px-3 py-2 text-xs text-gray-500">
                                                {index + 1}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-gray-600">
                                                {receiver.email}
                                            </td>
                                            {customerVariables
                                                .filter(
                                                    (v) => v.name !== 'email'
                                                )
                                                .map((v) => (
                                                    <td
                                                        key={v.name}
                                                        className="px-3 py-2 text-xs text-gray-600"
                                                    >
                                                        {receiver[v.name] ||
                                                            '-'}
                                                    </td>
                                                ))}
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                        {receivers.length > 50 && (
                            <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500 text-center">
                                {t('showing_first_50_of', {
                                    total: receivers.length,
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Empty state */}
            {receivers.length === 0 && !error && (
                <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <FileSpreadsheet
                        size={40}
                        className="mx-auto text-gray-300 mb-2"
                    />
                    <Typogaphy className="text-sm text-gray-500">
                        {t('mailerCampaign:no_file_uploaded')}
                    </Typogaphy>
                    <p className="text-xs text-gray-400 mt-1">
                        {t('mailerCampaign:upload_file_description')}
                    </p>
                </div>
            )}
        </div>
    )
}

export default ReceiverImporter
